
import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}
 /**
 * ActivityType
 *
 * A system-seeded classification of the kind of work a firm performs, such as Call, Email, Draft Document, Court, or Legal Research. Each type belongs to an activity category (Administrative, Client, Financials, Human Resources, Marketing, or Sales) and carries display metadata — an icon, a grammatical modifier word (e.g. 'with', 'for', 'to'), and optional template notes pre-filled on invoice line items. Tasks and template tasks are tagged with one or more activity types, which drives time-tracking classification and billing codes on finalized invoices.
 */
class ActivityType extends Base implements EntityType {
    activityTypeId: uuid;
    name: string;
    activityCategoryId?: uuid | null;
    documentation?: string | null;
    templateName?: string | null;
    templateNotes?: string | null;
    modifier?: string | null;
    icon?: string | null;

    /**
     * Create a new ActivityType, with an (optional) JSON object or ActivityType class
     *
     * @param {Object|ActivityType} doc - Can be either a simple JSON object or a ActivityType class used to initialize this object's fields
     * @constructor
     */
    constructor(doc: typeof ActivityType | any) {
        super(doc);
        
        // @ts-ignore
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: 'C',
            entityId: null,
            entityType: null,
            activityTypeId: genUUID(),
            name: null,
        };
        
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || 'activityType';
        // @ts-ignore
        this.activityTypeId = doc._activityTypeId || doc.activityTypeId || doc.activitytypeid || genUUID();
        // @ts-ignore
        this.name = doc._name || doc.name || null;
        // @ts-ignore
        this.activityCategoryId = doc._activityCategoryId || doc.activityCategoryId || doc.activitycategoryid || null;
        // @ts-ignore
        this.documentation = doc._documentation || doc.documentation || null;
        // @ts-ignore
        this.templateName = doc._templateName || doc.templateName || doc.templatename || null;
        // @ts-ignore
        this.templateNotes = doc._templateNotes || doc.templateNotes || doc.templatenotes || null;
        // @ts-ignore
        this.modifier = doc._modifier || doc.modifier || null;
        // @ts-ignore
        this.icon = doc._icon || doc.icon || null;

    }
    /**
     * @returns {uuid} This is a generic pointer to the activityTypeId across all entity types
     */
    get id(): uuid {
        return this.activityTypeId;
    }


    /**
     * Get a list of pk fields (primary keys)
     *
     * @returns {SchemaField[]}
     */
    static override getPkFields(): SchemaField[] {
        return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique ID for activity type"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}];
    }
    /**
     * TODO: Added just to handle react-select issues.  Can remove when remedied.
     */
    toLowerCase() {
        return this.activityTypeId;
    }
    
    /**
     * Get a list of non-reference array fields in the schema
     *
     * @return {Array.<{field: string, entity: string}>}
     */
    static override getNonRefArrayFields(): string[] {
        return [];
    }

    /**
     * Default entries are Outlaw-supplied ActivityType entries
     * @static
     * @return {object[]}
     */
    static override getDefaultEntries(): ActivityType[] {
        return [
        {
                "activityTypeId": "2d0fa4de-2740-4a73-b577-d1f8a9606c8f",
                "name": "File Documents",
                "modifier": "for",
                "icon": "FOLDERS",
                "activityCategoryId": "ca1a6abc-2f87-4d4a-9e2a-ea36c944d0c6",
                "status": "C"
        },
        {
                "activityTypeId": "7ce1a02a-c4a7-49dd-96a8-16e9c56a8604",
                "name": "Scan Documents",
                "modifier": "for",
                "icon": "SCAN",
                "activityCategoryId": "ca1a6abc-2f87-4d4a-9e2a-ea36c944d0c6",
                "status": "C"
        },
        {
                "activityTypeId": "223ea098-1ccc-4cad-883b-3a2af5249fd9",
                "name": "Print Documents",
                "modifier": "for",
                "icon": "PRINT",
                "activityCategoryId": "ca1a6abc-2f87-4d4a-9e2a-ea36c944d0c6",
                "status": "C"
        },
        {
                "activityTypeId": "1a5efc07-b5e0-4561-ad5c-d51ee85f602c",
                "name": "Call",
                "modifier": "with",
                "icon": "PHONE",
                "activityCategoryId": "ca1a6abc-2f87-4d4a-9e2a-ea36c944d0c6",
                "status": "C"
        },
        {
                "activityTypeId": "b5e4d326-ef56-4729-93f3-7fa4f56dcc7a",
                "name": "Email",
                "modifier": "to",
                "icon": "EMAIL",
                "activityCategoryId": "ca1a6abc-2f87-4d4a-9e2a-ea36c944d0c6",
                "status": "C"
        },
        {
                "activityTypeId": "38e49a60-fcc4-4d07-9fb7-0014d2929ba3",
                "name": "Mail",
                "modifier": "to/from",
                "icon": "MAIL",
                "activityCategoryId": "ca1a6abc-2f87-4d4a-9e2a-ea36c944d0c6",
                "status": "C"
        },
        {
                "activityTypeId": "ef14bc8a-fdcd-45de-8d36-8ee6fc5b0c57",
                "name": "Fax",
                "modifier": "to/from",
                "icon": "FAX",
                "activityCategoryId": "ca1a6abc-2f87-4d4a-9e2a-ea36c944d0c6",
                "status": "C"
        },
        {
                "activityTypeId": "f3647184-6af9-4161-a49a-35f7dd6b2cfa",
                "name": "Meet",
                "modifier": "with",
                "icon": "HANDSHAKE",
                "activityCategoryId": "ca1a6abc-2f87-4d4a-9e2a-ea36c944d0c6",
                "status": "C"
        },
        {
                "activityTypeId": "fb50d832-d99d-11e9-8a34-2a2ae2dbcce4",
                "name": "Planning",
                "modifier": "with",
                "icon": "PLAN",
                "activityCategoryId": "ca1a6abc-2f87-4d4a-9e2a-ea36c944d0c6",
                "status": "C"
        },
        {
                "activityTypeId": "12d17611-a916-45af-8677-05eb7af2ec14",
                "name": "Review Analytics",
                "modifier": "",
                "icon": "ANALYTICS",
                "activityCategoryId": "ca1a6abc-2f87-4d4a-9e2a-ea36c944d0c6",
                "status": "C"
        },
        {
                "activityTypeId": "f209cb56-e61c-4403-a029-4072f50c4108",
                "name": "File Documents",
                "modifier": "for",
                "icon": "FOLDERS",
                "activityCategoryId": "a8415c01-8d32-4620-bdc1-6832c6227c25",
                "status": "C"
        },
        {
                "activityTypeId": "6c87d6f3-5f56-4acf-907f-862ed5e515d4",
                "name": "Scan Documents",
                "modifier": "for",
                "icon": "SCAN",
                "activityCategoryId": "a8415c01-8d32-4620-bdc1-6832c6227c25",
                "status": "C"
        },
        {
                "activityTypeId": "49766cb2-6fd5-4acd-a3f7-9a54da0821bc",
                "name": "Print Documents",
                "modifier": "for",
                "icon": "PRINT",
                "activityCategoryId": "a8415c01-8d32-4620-bdc1-6832c6227c25",
                "status": "C"
        },
        {
                "activityTypeId": "88f233e1-62e0-4399-878e-21585089704c",
                "name": "Notarize Document",
                "modifier": "for",
                "icon": "STAMP",
                "activityCategoryId": "a8415c01-8d32-4620-bdc1-6832c6227c25",
                "status": "C"
        },
        {
                "activityTypeId": "94638743-c02c-41a2-8911-f4c11c8d0a9c",
                "name": "Draft Document",
                "modifier": "for",
                "icon": "PENCIL",
                "activityCategoryId": "a8415c01-8d32-4620-bdc1-6832c6227c25",
                "status": "C"
        },
        {
                "activityTypeId": "bdc8bcf2-8149-4937-9796-5048813f67e0",
                "name": "Analyze Document",
                "modifier": "for",
                "icon": "ANALYTICS",
                "activityCategoryId": "a8415c01-8d32-4620-bdc1-6832c6227c25",
                "status": "C"
        },
        {
                "activityTypeId": "73837c37-f88c-4ac7-8703-b9190f5832f6",
                "name": "Redact Document",
                "modifier": "for",
                "icon": "REDACT",
                "activityCategoryId": "a8415c01-8d32-4620-bdc1-6832c6227c25",
                "status": "C"
        },
        {
                "activityTypeId": "9a644f42-18d0-4009-8210-a841b7091af4",
                "name": "Legal Research",
                "modifier": "for",
                "icon": "RESEARCH",
                "activityCategoryId": "a8415c01-8d32-4620-bdc1-6832c6227c25",
                "status": "C"
        },
        {
                "activityTypeId": "0bc31092-a66f-4680-9501-c2df461e0c1e",
                "name": "Call",
                "modifier": "with",
                "icon": "PHONE",
                "activityCategoryId": "a8415c01-8d32-4620-bdc1-6832c6227c25",
                "status": "C"
        },
        {
                "activityTypeId": "50644a78-8d40-4c8a-a5ee-c4d2f2c45cc3",
                "name": "Email",
                "modifier": "to",
                "icon": "EMAIL",
                "activityCategoryId": "a8415c01-8d32-4620-bdc1-6832c6227c25",
                "status": "C"
        },
        {
                "activityTypeId": "84cdb714-c6f5-4d91-875e-b263ec67c835",
                "name": "Mail",
                "modifier": "to/from",
                "icon": "MAIL",
                "activityCategoryId": "a8415c01-8d32-4620-bdc1-6832c6227c25",
                "status": "C"
        },
        {
                "activityTypeId": "a6e7f849-3766-4793-8a20-7148cd32f3b8",
                "name": "Fax",
                "modifier": "to/from",
                "icon": "FAX",
                "activityCategoryId": "a8415c01-8d32-4620-bdc1-6832c6227c25",
                "status": "C"
        },
        {
                "activityTypeId": "66943013-0625-440d-b327-fa77d420b3ea",
                "name": "Meet",
                "modifier": "with",
                "icon": "HANDSHAKE",
                "activityCategoryId": "a8415c01-8d32-4620-bdc1-6832c6227c25",
                "status": "C"
        },
        {
                "activityTypeId": "ba578332-52bc-44c8-be39-575aa4c07fa8",
                "name": "Confer",
                "modifier": "with",
                "icon": "USERS",
                "activityCategoryId": "a8415c01-8d32-4620-bdc1-6832c6227c25",
                "status": "C"
        },
        {
                "activityTypeId": "1c51fbf5-c27d-4956-960f-e0476a0cc90e",
                "name": "Deposition",
                "modifier": "for",
                "icon": "QUESTION",
                "activityCategoryId": "a8415c01-8d32-4620-bdc1-6832c6227c25",
                "status": "C"
        },
        {
                "activityTypeId": "f3b9cda2-6af9-4799-81fe-378dbfb796a8",
                "name": "Discovery",
                "modifier": "for",
                "icon": "RESEARCH",
                "activityCategoryId": "a8415c01-8d32-4620-bdc1-6832c6227c25",
                "status": "C"
        },
        {
                "activityTypeId": "c091c6e2-4763-41e3-97f9-95e9961e605c",
                "name": "Mediation",
                "modifier": "for",
                "icon": "HANDSHAKE",
                "activityCategoryId": "a8415c01-8d32-4620-bdc1-6832c6227c25",
                "status": "C"
        },
        {
                "activityTypeId": "e7a17c95-d225-45bb-a2f7-1bcbb7c13eca",
                "name": "Pretrials",
                "modifier": "for",
                "icon": "GAVEL",
                "activityCategoryId": "a8415c01-8d32-4620-bdc1-6832c6227c25",
                "status": "C"
        },
        {
                "activityTypeId": "e6bd7fcd-e223-4def-bc69-7cdebebfdc60",
                "name": "Court",
                "modifier": "for",
                "icon": "GAVEL",
                "activityCategoryId": "a8415c01-8d32-4620-bdc1-6832c6227c25",
                "status": "C"
        },
        {
                "activityTypeId": "be97072a-aa2d-4da9-a14e-ca7df751aae5",
                "name": "Witness Preparation",
                "modifier": "for",
                "icon": "GAVEL",
                "activityCategoryId": "a8415c01-8d32-4620-bdc1-6832c6227c25",
                "status": "C"
        },
        {
                "activityTypeId": "058c997f-7799-4c44-aabe-9a3f928bde25",
                "name": "Conflict Check",
                "modifier": "with",
                "icon": "CONFLICT",
                "activityCategoryId": "a8415c01-8d32-4620-bdc1-6832c6227c25",
                "status": "C"
        },
        {
                "activityTypeId": "4e81a9fb-2c57-4fa8-a4a1-7045d3d2f38d",
                "name": "Prepare Invoices",
                "modifier": "with",
                "icon": "INVOICE",
                "activityCategoryId": "0bc3ad94-8786-425f-bcbc-2bad281c0ad6",
                "status": "C"
        },
        {
                "activityTypeId": "1ee8de9e-7c7d-4c9f-825c-099b30bd53b4",
                "name": "Print Invoices",
                "modifier": "with",
                "icon": "PRINT",
                "activityCategoryId": "0bc3ad94-8786-425f-bcbc-2bad281c0ad6",
                "status": "C"
        },
        {
                "activityTypeId": "1ee8de9e-7c7d-4c9f-825c-099b30bd53b4",
                "name": "Send Invoices",
                "modifier": "with",
                "icon": "EMAIL",
                "activityCategoryId": "0bc3ad94-8786-425f-bcbc-2bad281c0ad6",
                "status": "C"
        },
        {
                "activityTypeId": "75f1d79c-236f-445e-bf5d-7d88d7560806",
                "name": "Generate Accounts Receivable Aging Report",
                "modifier": "",
                "icon": "ANALYTICS",
                "activityCategoryId": "0bc3ad94-8786-425f-bcbc-2bad281c0ad6",
                "status": "C"
        },
        {
                "activityTypeId": "21ccdeec-5da0-4407-b930-ad15d93afec2",
                "name": "Generate Income Statement",
                "modifier": "",
                "icon": "ANALYTICS",
                "activityCategoryId": "0bc3ad94-8786-425f-bcbc-2bad281c0ad6",
                "status": "C"
        },
        {
                "activityTypeId": "6d89ed8f-2dc9-48d1-ad6e-0f00d177bd7f",
                "name": "Generate Cash Flow Statement",
                "modifier": "",
                "icon": "ANALYTICS",
                "activityCategoryId": "0bc3ad94-8786-425f-bcbc-2bad281c0ad6",
                "status": "C"
        },
        {
                "activityTypeId": "24d62ee4-7efb-4ef6-9d8f-a8b62a90e4c2",
                "name": "Generate Balance Sheet",
                "modifier": "",
                "icon": "ANALYTICS",
                "activityCategoryId": "0bc3ad94-8786-425f-bcbc-2bad281c0ad6",
                "status": "C"
        },
        {
                "activityTypeId": "8a6be7cc-4e74-4e3c-a8e5-a18a932ac3cb",
                "name": "Generate Budget vs Actuals",
                "modifier": "",
                "icon": "ANALYTICS",
                "activityCategoryId": "0bc3ad94-8786-425f-bcbc-2bad281c0ad6",
                "status": "C"
        },
        {
                "activityTypeId": "9abbcd49-d764-481e-8569-8153418a2d36",
                "name": "Generate Profit and Loss",
                "modifier": "",
                "icon": "ANALYTICS",
                "activityCategoryId": "0bc3ad94-8786-425f-bcbc-2bad281c0ad6",
                "status": "C"
        },
        {
                "activityTypeId": "8f286ebf-a0e1-423a-a93e-d9103dac298d",
                "name": "Review Accounts Receivable Aging Report",
                "modifier": "",
                "icon": "ANALYTICS",
                "activityCategoryId": "0bc3ad94-8786-425f-bcbc-2bad281c0ad6",
                "status": "C"
        },
        {
                "activityTypeId": "0d920ce9-a43c-4ff8-beaa-560baba50d13",
                "name": "Review Income Statement",
                "modifier": "",
                "icon": "ANALYTICS",
                "activityCategoryId": "0bc3ad94-8786-425f-bcbc-2bad281c0ad6",
                "status": "C"
        },
        {
                "activityTypeId": "42b47966-4b0a-4c20-8c2f-914b2a4f26b2",
                "name": "Review Cash Flow Statement",
                "modifier": "",
                "icon": "ANALYTICS",
                "activityCategoryId": "0bc3ad94-8786-425f-bcbc-2bad281c0ad6",
                "status": "C"
        },
        {
                "activityTypeId": "a45d551f-a964-44f4-9b1b-4211deb7bf91",
                "name": "Review Balance Sheet",
                "modifier": "",
                "icon": "ANALYTICS",
                "activityCategoryId": "0bc3ad94-8786-425f-bcbc-2bad281c0ad6",
                "status": "C"
        },
        {
                "activityTypeId": "4e8fd5d3-3f92-4679-9b8c-574f57dc46c4",
                "name": "Review Budget vs Actuals",
                "modifier": "",
                "icon": "ANALYTICS",
                "activityCategoryId": "0bc3ad94-8786-425f-bcbc-2bad281c0ad6",
                "status": "C"
        },
        {
                "activityTypeId": "bbb6d1ca-2df1-4703-8f1f-451bc7120894",
                "name": "Review Profit and Loss",
                "modifier": "",
                "icon": "ANALYTICS",
                "activityCategoryId": "0bc3ad94-8786-425f-bcbc-2bad281c0ad6",
                "status": "C"
        },
        {
                "activityTypeId": "b55e879b-5aff-4469-8a7d-99d8a0b49170",
                "name": "Planning",
                "modifier": "",
                "icon": "PLAN",
                "activityCategoryId": "0bc3ad94-8786-425f-bcbc-2bad281c0ad6",
                "status": "C"
        },
        {
                "activityTypeId": "773a7ee5-08db-44ad-a4fc-ccdfb001d1d5",
                "name": "Call",
                "modifier": "with",
                "icon": "PHONE",
                "activityCategoryId": "0bc3ad94-8786-425f-bcbc-2bad281c0ad6",
                "status": "C"
        },
        {
                "activityTypeId": "61b5d6e8-ac3c-4a42-ba3e-edbaab826b12",
                "name": "Email",
                "modifier": "to",
                "icon": "EMAIL",
                "activityCategoryId": "0bc3ad94-8786-425f-bcbc-2bad281c0ad6",
                "status": "C"
        },
        {
                "activityTypeId": "152da0de-b953-4990-8af7-8ad8a32e5744",
                "name": "Mail",
                "modifier": "to/from",
                "icon": "MAIL",
                "activityCategoryId": "0bc3ad94-8786-425f-bcbc-2bad281c0ad6",
                "status": "C"
        },
        {
                "activityTypeId": "33078db0-3a70-43ed-940f-7d7996aa6568",
                "name": "Fax",
                "modifier": "to/from",
                "icon": "FAX",
                "activityCategoryId": "0bc3ad94-8786-425f-bcbc-2bad281c0ad6",
                "status": "C"
        },
        {
                "activityTypeId": "67a23464-e62d-4d9a-95f2-843590115435",
                "name": "Meet",
                "modifier": "with",
                "icon": "HANDSHAKE",
                "activityCategoryId": "0bc3ad94-8786-425f-bcbc-2bad281c0ad6",
                "status": "C"
        },
        {
                "activityTypeId": "5c1d68ef-4df8-4201-aac9-4cc9b8421842",
                "name": "Prepare for Employee Review",
                "modifier": "of",
                "icon": "WRITE",
                "activityCategoryId": "fabc9370-8ead-4c82-9a4d-a37f9a692d92",
                "status": "C"
        },
        {
                "activityTypeId": "3b217c1c-2012-436a-a0c5-ce37509af998",
                "name": "Employee Review",
                "modifier": "with",
                "icon": "USER",
                "activityCategoryId": "fabc9370-8ead-4c82-9a4d-a37f9a692d92",
                "status": "C"
        },
        {
                "activityTypeId": "99a268cc-da6d-4cf2-aa62-635fb61ceeb6",
                "name": "CLE",
                "modifier": "",
                "icon": "LEARN",
                "activityCategoryId": "fabc9370-8ead-4c82-9a4d-a37f9a692d92",
                "status": "C"
        },
        {
                "activityTypeId": "dcd75aca-6f83-452b-bd0a-9cca9dca348b",
                "name": "Draft Job Description",
                "modifier": "",
                "icon": "WRITE",
                "activityCategoryId": "fabc9370-8ead-4c82-9a4d-a37f9a692d92",
                "status": "C"
        },
        {
                "activityTypeId": "9c0f98e9-dd1f-4aaa-8063-eb7b3fc5d48b",
                "name": "Post Job Description",
                "modifier": "",
                "icon": "RESUME",
                "activityCategoryId": "fabc9370-8ead-4c82-9a4d-a37f9a692d92",
                "status": "C"
        },
        {
                "activityTypeId": "f826863c-0ad3-4816-a6d8-3b6b89aa2a9f",
                "name": "Review Resume",
                "modifier": "",
                "icon": "RESUME",
                "activityCategoryId": "fabc9370-8ead-4c82-9a4d-a37f9a692d92",
                "status": "C"
        },
        {
                "activityTypeId": "08889721-7efb-4967-b890-8ca4dd3f83d6",
                "name": "Prepare for Candidate Interview",
                "modifier": "",
                "icon": "READ",
                "activityCategoryId": "fabc9370-8ead-4c82-9a4d-a37f9a692d92",
                "status": "C"
        },
        {
                "activityTypeId": "8dbc6482-db99-44f8-b4a9-d920099c0750",
                "name": "Interview Candidate",
                "modifier": "",
                "icon": "HANDSHAKE",
                "activityCategoryId": "fabc9370-8ead-4c82-9a4d-a37f9a692d92",
                "status": "C"
        },
        {
                "activityTypeId": "4d87f554-efdb-4c2e-9878-73a71ba3a438",
                "name": "Review Candidate",
                "modifier": "",
                "icon": "READ",
                "activityCategoryId": "fabc9370-8ead-4c82-9a4d-a37f9a692d92",
                "status": "C"
        },
        {
                "activityTypeId": "bc7ef80b-0d12-4e64-b85e-705bd4d6079e",
                "name": "Make Job Offer",
                "modifier": "to",
                "icon": "SALES",
                "activityCategoryId": "fabc9370-8ead-4c82-9a4d-a37f9a692d92",
                "status": "C"
        },
        {
                "activityTypeId": "57ab5ac1-181e-483e-b078-55264da3f962",
                "name": "Employee Time",
                "modifier": "with",
                "icon": "USER",
                "activityCategoryId": "fabc9370-8ead-4c82-9a4d-a37f9a692d92",
                "status": "C"
        },
        {
                "activityTypeId": "47d58bbc-c7de-4e73-a707-a1900007b86f",
                "name": "Review Analytics",
                "modifier": "with",
                "icon": "ANALYTICS",
                "activityCategoryId": "fabc9370-8ead-4c82-9a4d-a37f9a692d92",
                "status": "C"
        },
        {
                "activityTypeId": "7a87b303-ce41-428e-98b2-bfdca74d2a83",
                "name": "Call",
                "modifier": "with",
                "icon": "PHONE",
                "activityCategoryId": "f8f862c4-fa5c-49a7-aea3-9664d085fbe7",
                "status": "C"
        },
        {
                "activityTypeId": "10a0596f-7f82-4615-a184-ba83b9f888a4",
                "name": "Email",
                "modifier": "to",
                "icon": "EMAIL",
                "activityCategoryId": "f8f862c4-fa5c-49a7-aea3-9664d085fbe7",
                "status": "C"
        },
        {
                "activityTypeId": "e6fd32af-de93-481c-8f2e-d127ef161add",
                "name": "Mail",
                "modifier": "to/from",
                "icon": "MAIL",
                "activityCategoryId": "f8f862c4-fa5c-49a7-aea3-9664d085fbe7",
                "status": "C"
        },
        {
                "activityTypeId": "9d81a254-3b41-4ab4-a690-429ae6e79891",
                "name": "Fax",
                "modifier": "to/from",
                "icon": "FAX",
                "activityCategoryId": "f8f862c4-fa5c-49a7-aea3-9664d085fbe7",
                "status": "C"
        },
        {
                "activityTypeId": "52c4d090-6a75-45ad-b1d0-f5e3b8240166",
                "name": "Meet",
                "modifier": "with",
                "icon": "HANDSHAKE",
                "activityCategoryId": "f8f862c4-fa5c-49a7-aea3-9664d085fbe7",
                "status": "C"
        },
        {
                "activityTypeId": "f2cbdd0e-f727-48ba-99e3-a771f0399bdf",
                "name": "Smile and Dial",
                "modifier": "with",
                "icon": "SMILE_DIAL",
                "activityCategoryId": "f8f862c4-fa5c-49a7-aea3-9664d085fbe7",
                "status": "C"
        },
        {
                "activityTypeId": "81a2ceb8-1d1f-4b49-92e2-bb759335c267",
                "name": "Planning",
                "modifier": "with",
                "icon": "PLAN",
                "activityCategoryId": "f8f862c4-fa5c-49a7-aea3-9664d085fbe7",
                "status": "C"
        },
        {
                "activityTypeId": "6f2832c0-c5c5-47d3-8ea3-ddc0957c4b40",
                "name": "Review Analytics",
                "modifier": "",
                "icon": "ANALYTICS",
                "activityCategoryId": "f8f862c4-fa5c-49a7-aea3-9664d085fbe7",
                "status": "C"
        },
        {
                "activityTypeId": "aea93c81-c896-4116-82f0-99864deef232",
                "name": "Consultation",
                "modifier": "with",
                "icon": "HANDSHAKE",
                "activityCategoryId": "f8f862c4-fa5c-49a7-aea3-9664d085fbe7",
                "status": "C"
        },
        {
                "activityTypeId": "6c996870-a020-47d0-a644-9068df7199cd",
                "name": "Follow Up",
                "modifier": "with",
                "icon": "HANDSHAKE",
                "activityCategoryId": "f8f862c4-fa5c-49a7-aea3-9664d085fbe7",
                "status": "C"
        },
        {
                "activityTypeId": "0c4e57ba-cb95-49c7-b3f8-6a5e385daf08",
                "name": "Conflict Check",
                "modifier": "with",
                "icon": "CONFLICT",
                "activityCategoryId": "f8f862c4-fa5c-49a7-aea3-9664d085fbe7",
                "status": "C"
        },
        {
                "activityTypeId": "470031bd-f041-4543-8b01-6030ac9a7d76",
                "name": "Call",
                "modifier": "with",
                "icon": "PHONE",
                "activityCategoryId": "038bd87f-2d35-431e-b5c8-7a31cf31a9f6",
                "status": "C"
        },
        {
                "activityTypeId": "71c9f406-1c88-4757-aca9-ac8c015abb98",
                "name": "Email",
                "modifier": "to",
                "icon": "EMAIL",
                "activityCategoryId": "038bd87f-2d35-431e-b5c8-7a31cf31a9f6",
                "status": "C"
        },
        {
                "activityTypeId": "9cf0e68c-854d-4901-b73a-9172dbfab0be",
                "name": "Mail",
                "modifier": "to/from",
                "icon": "MAIL",
                "activityCategoryId": "038bd87f-2d35-431e-b5c8-7a31cf31a9f6",
                "status": "C"
        },
        {
                "activityTypeId": "b74728af-4411-4607-97e7-0eb024182286",
                "name": "Fax",
                "modifier": "to/from",
                "icon": "FAX",
                "activityCategoryId": "038bd87f-2d35-431e-b5c8-7a31cf31a9f6",
                "status": "C"
        },
        {
                "activityTypeId": "c8d9e780-8e2e-469e-a59c-2bd35408c4ff",
                "name": "Design",
                "modifier": "with",
                "icon": "PENCIL",
                "activityCategoryId": "038bd87f-2d35-431e-b5c8-7a31cf31a9f6",
                "status": "C"
        },
        {
                "activityTypeId": "f00cd735-095b-4314-b95f-a6712e9c586f",
                "name": "Meet",
                "modifier": "with",
                "icon": "HANDSHAKE",
                "activityCategoryId": "038bd87f-2d35-431e-b5c8-7a31cf31a9f6",
                "status": "C"
        },
        {
                "activityTypeId": "af48a3f7-f4a5-4294-b7d4-b00bfcd3a0fd",
                "name": "Planning",
                "modifier": "with",
                "icon": "PLAN",
                "activityCategoryId": "038bd87f-2d35-431e-b5c8-7a31cf31a9f6",
                "status": "C"
        },
        {
                "activityTypeId": "726b0ac9-b0b7-4958-b08e-1f660e5adb2e",
                "name": "Presentation",
                "modifier": "with",
                "icon": "PRESENT",
                "activityCategoryId": "038bd87f-2d35-431e-b5c8-7a31cf31a9f6",
                "status": "C"
        },
        {
                "activityTypeId": "8b691fe1-a5c1-48fb-ab1f-63f597984841",
                "name": "Referral Development",
                "modifier": "with",
                "icon": "REFERRAL_OUT",
                "activityCategoryId": "038bd87f-2d35-431e-b5c8-7a31cf31a9f6",
                "status": "C"
        },
        {
                "activityTypeId": "bf8b75dc-e36a-4561-aeb3-59504c5899ae",
                "name": "Review Analytics",
                "modifier": "",
                "icon": "ANALYTICS",
                "activityCategoryId": "038bd87f-2d35-431e-b5c8-7a31cf31a9f6",
                "status": "C"
        }
].map(e => new ActivityType(e));
    }

    /**
     * Get a reference to the schema entity.
     * @static
     * @return {object} - the original schema
     */
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
            entity: 'activityType',
            table: 'ACTIVITY_TYPE',
            comment: `A system-seeded classification of the kind of work a firm performs, such as Call, Email, Draft Document, Court, or Legal Research. Each type belongs to an activity category (Administrative, Client, Financials, Human Resources, Marketing, or Sales) and carries display metadata — an icon, a grammatical modifier word (e.g. 'with', 'for', 'to'), and optional template notes pre-filled on invoice line items. Tasks and template tasks are tagged with one or more activity types, which drives time-tracking classification and billing codes on finalized invoices.`,
            fields: {
                activityTypeId: {
                    type: 'uuid',
                    required: true,
                    pk: true,
                    default: '__UUID__',
                    comment: 'Unique ID for activity type'
                },
                // Name to show in the drop-down
                name: {
                    type: 'string',
                    required: true,
                    comment: 'Name to show in the drop-down'
                },
                // Category, like document or communication
                activityCategoryId: {
                    type: 'uuid',
                    entityType: 'activityCategory',
                    comment: 'Category, like document or communication'
                },
                // Documentation for the user (What is this activity type, and when do I use it?)
                // This should be rich text.
                documentation: {
                    type: 'string',
                    comment: 'Description for the user'
                },
                // Template to create entry name, like 'Met with [participants]'
                // NOTE: This is not used, it can be safely deleted
                templateName: {
                    type: 'string',
                    comment: 'Unused, can delete'
                },
                // Template text for notes in an invoice, like mandatory wording and explanation.
                templateNotes: {
                    type: 'string',
                    comment: 'Template notes for invoice entries'
                },
                // When formatting, what comes after the name?  e.g. Call 'with', or 'Scan documents for'
                modifier: {
                    type: 'string',
                    comment: 'When used in formatting, what word should be added after the type? e.g. for, with'
                },
                // An icon to associate (ex. 'ACTIVITY')
                icon: {
                    type: 'string',
                    comment: 'The Icons.STATIC field with the associated icon'
                }
            },
            entries: [
                // Administrative
                {
                    activityTypeId: '2d0fa4de-2740-4a73-b577-d1f8a9606c8f',
                    name: 'File Documents',
                    modifier: 'for',
                    icon: 'FOLDERS',
                    activityCategoryId: 'ca1a6abc-2f87-4d4a-9e2a-ea36c944d0c6'
                },
                {
                    activityTypeId: '7ce1a02a-c4a7-49dd-96a8-16e9c56a8604',
                    name: 'Scan Documents',
                    modifier: 'for',
                    icon: 'SCAN',
                    activityCategoryId: 'ca1a6abc-2f87-4d4a-9e2a-ea36c944d0c6'
                },
                {
                    activityTypeId: '223ea098-1ccc-4cad-883b-3a2af5249fd9',
                    name: 'Print Documents',
                    modifier: 'for',
                    icon: 'PRINT',
                    activityCategoryId: 'ca1a6abc-2f87-4d4a-9e2a-ea36c944d0c6'
                },
                {
                    activityTypeId: '1a5efc07-b5e0-4561-ad5c-d51ee85f602c',
                    name: 'Call',
                    modifier: 'with',
                    icon: 'PHONE',
                    activityCategoryId: 'ca1a6abc-2f87-4d4a-9e2a-ea36c944d0c6'
                },
                {
                    activityTypeId: 'b5e4d326-ef56-4729-93f3-7fa4f56dcc7a',
                    name: 'Email',
                    modifier: 'to',
                    icon: 'EMAIL',
                    activityCategoryId: 'ca1a6abc-2f87-4d4a-9e2a-ea36c944d0c6'
                },
                {
                    activityTypeId: '38e49a60-fcc4-4d07-9fb7-0014d2929ba3',
                    name: 'Mail',
                    modifier: 'to/from',
                    icon: 'MAIL',
                    activityCategoryId: 'ca1a6abc-2f87-4d4a-9e2a-ea36c944d0c6'
                },
                {
                    activityTypeId: 'ef14bc8a-fdcd-45de-8d36-8ee6fc5b0c57',
                    name: 'Fax',
                    modifier: 'to/from',
                    icon: 'FAX',
                    activityCategoryId: 'ca1a6abc-2f87-4d4a-9e2a-ea36c944d0c6'
                },
                {
                    activityTypeId: 'f3647184-6af9-4161-a49a-35f7dd6b2cfa',
                    name: 'Meet',
                    modifier: 'with',
                    icon: 'HANDSHAKE',
                    activityCategoryId: 'ca1a6abc-2f87-4d4a-9e2a-ea36c944d0c6'
                },
                {
                    activityTypeId: 'fb50d832-d99d-11e9-8a34-2a2ae2dbcce4',
                    name: 'Planning',
                    modifier: 'with',
                    icon: 'PLAN',
                    activityCategoryId: 'ca1a6abc-2f87-4d4a-9e2a-ea36c944d0c6'
                },
                {
                    activityTypeId: '12d17611-a916-45af-8677-05eb7af2ec14',
                    name: 'Review Analytics',
                    modifier: '',
                    icon: 'ANALYTICS',
                    activityCategoryId: 'ca1a6abc-2f87-4d4a-9e2a-ea36c944d0c6'
                },
                // Client
                {
                    activityTypeId: 'f209cb56-e61c-4403-a029-4072f50c4108',
                    name: 'File Documents',
                    modifier: 'for',
                    icon: 'FOLDERS',
                    activityCategoryId: 'a8415c01-8d32-4620-bdc1-6832c6227c25'
                },
                {
                    activityTypeId: '6c87d6f3-5f56-4acf-907f-862ed5e515d4',
                    name: 'Scan Documents',
                    modifier: 'for',
                    icon: 'SCAN',
                    activityCategoryId: 'a8415c01-8d32-4620-bdc1-6832c6227c25'
                },
                {
                    activityTypeId: '49766cb2-6fd5-4acd-a3f7-9a54da0821bc',
                    name: 'Print Documents',
                    modifier: 'for',
                    icon: 'PRINT',
                    activityCategoryId: 'a8415c01-8d32-4620-bdc1-6832c6227c25'
                },
                {
                    activityTypeId: '88f233e1-62e0-4399-878e-21585089704c',
                    name: 'Notarize Document',
                    modifier: 'for',
                    icon: 'STAMP',
                    activityCategoryId: 'a8415c01-8d32-4620-bdc1-6832c6227c25'
                },
                {
                    activityTypeId: '94638743-c02c-41a2-8911-f4c11c8d0a9c',
                    name: 'Draft Document',
                    modifier: 'for',
                    icon: 'PENCIL',
                    activityCategoryId: 'a8415c01-8d32-4620-bdc1-6832c6227c25'
                },
                {
                    activityTypeId: 'bdc8bcf2-8149-4937-9796-5048813f67e0',
                    name: 'Analyze Document',
                    modifier: 'for',
                    icon: 'ANALYTICS',
                    activityCategoryId: 'a8415c01-8d32-4620-bdc1-6832c6227c25'
                },
                {
                    activityTypeId: '73837c37-f88c-4ac7-8703-b9190f5832f6',
                    name: 'Redact Document',
                    modifier: 'for',
                    icon: 'REDACT',
                    activityCategoryId: 'a8415c01-8d32-4620-bdc1-6832c6227c25'
                },
                {
                    activityTypeId: '9a644f42-18d0-4009-8210-a841b7091af4',
                    name: 'Legal Research',
                    modifier: 'for',
                    icon: 'RESEARCH',
                    activityCategoryId: 'a8415c01-8d32-4620-bdc1-6832c6227c25'
                },
                {
                    activityTypeId: '0bc31092-a66f-4680-9501-c2df461e0c1e',
                    name: 'Call',
                    modifier: 'with',
                    icon: 'PHONE',
                    activityCategoryId: 'a8415c01-8d32-4620-bdc1-6832c6227c25'
                },
                {
                    activityTypeId: '50644a78-8d40-4c8a-a5ee-c4d2f2c45cc3',
                    name: 'Email',
                    modifier: 'to',
                    icon: 'EMAIL',
                    activityCategoryId: 'a8415c01-8d32-4620-bdc1-6832c6227c25'
                },
                {
                    activityTypeId: '84cdb714-c6f5-4d91-875e-b263ec67c835',
                    name: 'Mail',
                    modifier: 'to/from',
                    icon: 'MAIL',
                    activityCategoryId: 'a8415c01-8d32-4620-bdc1-6832c6227c25'
                },
                {
                    activityTypeId: 'a6e7f849-3766-4793-8a20-7148cd32f3b8',
                    name: 'Fax',
                    modifier: 'to/from',
                    icon: 'FAX',
                    activityCategoryId: 'a8415c01-8d32-4620-bdc1-6832c6227c25'
                },
                {
                    activityTypeId: '66943013-0625-440d-b327-fa77d420b3ea',
                    name: 'Meet',
                    modifier: 'with',
                    icon: 'HANDSHAKE',
                    activityCategoryId: 'a8415c01-8d32-4620-bdc1-6832c6227c25'
                },
                {
                    activityTypeId: 'ba578332-52bc-44c8-be39-575aa4c07fa8',
                    name: 'Confer',
                    modifier: 'with',
                    icon: 'USERS',
                    activityCategoryId: 'a8415c01-8d32-4620-bdc1-6832c6227c25'
                },
                {
                    activityTypeId: '1c51fbf5-c27d-4956-960f-e0476a0cc90e',
                    name: 'Deposition',
                    modifier: 'for',
                    icon: 'QUESTION',
                    activityCategoryId: 'a8415c01-8d32-4620-bdc1-6832c6227c25'
                },
                {
                    activityTypeId: 'f3b9cda2-6af9-4799-81fe-378dbfb796a8',
                    name: 'Discovery',
                    modifier: 'for',
                    icon: 'RESEARCH',
                    activityCategoryId: 'a8415c01-8d32-4620-bdc1-6832c6227c25'
                },
                {
                    activityTypeId: 'c091c6e2-4763-41e3-97f9-95e9961e605c',
                    name: 'Mediation',
                    modifier: 'for',
                    icon: 'HANDSHAKE',
                    activityCategoryId: 'a8415c01-8d32-4620-bdc1-6832c6227c25'
                },
                {
                    activityTypeId: 'e7a17c95-d225-45bb-a2f7-1bcbb7c13eca',
                    name: 'Pretrials',
                    modifier: 'for',
                    icon: 'GAVEL',
                    activityCategoryId: 'a8415c01-8d32-4620-bdc1-6832c6227c25'
                },
                {
                    activityTypeId: 'e6bd7fcd-e223-4def-bc69-7cdebebfdc60',
                    name: 'Court',
                    modifier: 'for',
                    icon: 'GAVEL',
                    activityCategoryId: 'a8415c01-8d32-4620-bdc1-6832c6227c25'
                },
                {
                    activityTypeId: 'be97072a-aa2d-4da9-a14e-ca7df751aae5',
                    name: 'Witness Preparation',
                    modifier: 'for',
                    icon: 'GAVEL',
                    activityCategoryId: 'a8415c01-8d32-4620-bdc1-6832c6227c25'
                },
                {
                    activityTypeId: '058c997f-7799-4c44-aabe-9a3f928bde25',
                    name: 'Conflict Check',
                    modifier: 'with',
                    icon: 'CONFLICT',
                    activityCategoryId: 'a8415c01-8d32-4620-bdc1-6832c6227c25'
                },
                // Financials
                {
                    activityTypeId: '4e81a9fb-2c57-4fa8-a4a1-7045d3d2f38d',
                    name: 'Prepare Invoices',
                    modifier: 'with',
                    icon: 'INVOICE',
                    activityCategoryId: '0bc3ad94-8786-425f-bcbc-2bad281c0ad6'
                },
                {
                    activityTypeId: '1ee8de9e-7c7d-4c9f-825c-099b30bd53b4',
                    name: 'Print Invoices',
                    modifier: 'with',
                    icon: 'PRINT',
                    activityCategoryId: '0bc3ad94-8786-425f-bcbc-2bad281c0ad6'
                },
                {
                    activityTypeId: '1ee8de9e-7c7d-4c9f-825c-099b30bd53b4',
                    name: 'Send Invoices',
                    modifier: 'with',
                    icon: 'EMAIL',
                    activityCategoryId: '0bc3ad94-8786-425f-bcbc-2bad281c0ad6'
                },
                {
                    activityTypeId: '75f1d79c-236f-445e-bf5d-7d88d7560806',
                    name: 'Generate Accounts Receivable Aging Report',
                    modifier: '',
                    icon: 'ANALYTICS',
                    activityCategoryId: '0bc3ad94-8786-425f-bcbc-2bad281c0ad6'
                },
                {
                    activityTypeId: '21ccdeec-5da0-4407-b930-ad15d93afec2',
                    name: 'Generate Income Statement',
                    modifier: '',
                    icon: 'ANALYTICS',
                    activityCategoryId: '0bc3ad94-8786-425f-bcbc-2bad281c0ad6'
                },
                {
                    activityTypeId: '6d89ed8f-2dc9-48d1-ad6e-0f00d177bd7f',
                    name: 'Generate Cash Flow Statement',
                    modifier: '',
                    icon: 'ANALYTICS',
                    activityCategoryId: '0bc3ad94-8786-425f-bcbc-2bad281c0ad6'
                },
                {
                    activityTypeId: '24d62ee4-7efb-4ef6-9d8f-a8b62a90e4c2',
                    name: 'Generate Balance Sheet',
                    modifier: '',
                    icon: 'ANALYTICS',
                    activityCategoryId: '0bc3ad94-8786-425f-bcbc-2bad281c0ad6'
                },
                {
                    activityTypeId: '8a6be7cc-4e74-4e3c-a8e5-a18a932ac3cb',
                    name: 'Generate Budget vs Actuals',
                    modifier: '',
                    icon: 'ANALYTICS',
                    activityCategoryId: '0bc3ad94-8786-425f-bcbc-2bad281c0ad6'
                },
                {
                    activityTypeId: '9abbcd49-d764-481e-8569-8153418a2d36',
                    name: 'Generate Profit and Loss',
                    modifier: '',
                    icon: 'ANALYTICS',
                    activityCategoryId: '0bc3ad94-8786-425f-bcbc-2bad281c0ad6'
                },
                {
                    activityTypeId: '8f286ebf-a0e1-423a-a93e-d9103dac298d',
                    name: 'Review Accounts Receivable Aging Report',
                    modifier: '',
                    icon: 'ANALYTICS',
                    activityCategoryId: '0bc3ad94-8786-425f-bcbc-2bad281c0ad6'
                },
                {
                    activityTypeId: '0d920ce9-a43c-4ff8-beaa-560baba50d13',
                    name: 'Review Income Statement',
                    modifier: '',
                    icon: 'ANALYTICS',
                    activityCategoryId: '0bc3ad94-8786-425f-bcbc-2bad281c0ad6'
                },
                {
                    activityTypeId: '42b47966-4b0a-4c20-8c2f-914b2a4f26b2',
                    name: 'Review Cash Flow Statement',
                    modifier: '',
                    icon: 'ANALYTICS',
                    activityCategoryId: '0bc3ad94-8786-425f-bcbc-2bad281c0ad6'
                },
                {
                    activityTypeId: 'a45d551f-a964-44f4-9b1b-4211deb7bf91',
                    name: 'Review Balance Sheet',
                    modifier: '',
                    icon: 'ANALYTICS',
                    activityCategoryId: '0bc3ad94-8786-425f-bcbc-2bad281c0ad6'
                },
                {
                    activityTypeId: '4e8fd5d3-3f92-4679-9b8c-574f57dc46c4',
                    name: 'Review Budget vs Actuals',
                    modifier: '',
                    icon: 'ANALYTICS',
                    activityCategoryId: '0bc3ad94-8786-425f-bcbc-2bad281c0ad6'
                },
                {
                    activityTypeId: 'bbb6d1ca-2df1-4703-8f1f-451bc7120894',
                    name: 'Review Profit and Loss',
                    modifier: '',
                    icon: 'ANALYTICS',
                    activityCategoryId: '0bc3ad94-8786-425f-bcbc-2bad281c0ad6'
                },
                {
                    activityTypeId: 'b55e879b-5aff-4469-8a7d-99d8a0b49170',
                    name: 'Planning',
                    modifier: '',
                    icon: 'PLAN',
                    activityCategoryId: '0bc3ad94-8786-425f-bcbc-2bad281c0ad6'
                },
                {
                    activityTypeId: '773a7ee5-08db-44ad-a4fc-ccdfb001d1d5',
                    name: 'Call',
                    modifier: 'with',
                    icon: 'PHONE',
                    activityCategoryId: '0bc3ad94-8786-425f-bcbc-2bad281c0ad6'
                },
                {
                    activityTypeId: '61b5d6e8-ac3c-4a42-ba3e-edbaab826b12',
                    name: 'Email',
                    modifier: 'to',
                    icon: 'EMAIL',
                    activityCategoryId: '0bc3ad94-8786-425f-bcbc-2bad281c0ad6'
                },
                {
                    activityTypeId: '152da0de-b953-4990-8af7-8ad8a32e5744',
                    name: 'Mail',
                    modifier: 'to/from',
                    icon: 'MAIL',
                    activityCategoryId: '0bc3ad94-8786-425f-bcbc-2bad281c0ad6'
                },
                {
                    activityTypeId: '33078db0-3a70-43ed-940f-7d7996aa6568',
                    name: 'Fax',
                    modifier: 'to/from',
                    icon: 'FAX',
                    activityCategoryId: '0bc3ad94-8786-425f-bcbc-2bad281c0ad6'
                },
                {
                    activityTypeId: '67a23464-e62d-4d9a-95f2-843590115435',
                    name: 'Meet',
                    modifier: 'with',
                    icon: 'HANDSHAKE',
                    activityCategoryId: '0bc3ad94-8786-425f-bcbc-2bad281c0ad6'
                },
                // Human Resources
                {
                    activityTypeId: '5c1d68ef-4df8-4201-aac9-4cc9b8421842',
                    name: 'Prepare for Employee Review',
                    modifier: 'of',
                    icon: 'WRITE',
                    activityCategoryId: 'fabc9370-8ead-4c82-9a4d-a37f9a692d92'
                },
                {
                    activityTypeId: '3b217c1c-2012-436a-a0c5-ce37509af998',
                    name: 'Employee Review',
                    modifier: 'with',
                    icon: 'USER',
                    activityCategoryId: 'fabc9370-8ead-4c82-9a4d-a37f9a692d92'
                },
                {
                    activityTypeId: '99a268cc-da6d-4cf2-aa62-635fb61ceeb6',
                    name: 'CLE',
                    modifier: '',
                    icon: 'LEARN',
                    activityCategoryId: 'fabc9370-8ead-4c82-9a4d-a37f9a692d92'
                },
                {
                    activityTypeId: 'dcd75aca-6f83-452b-bd0a-9cca9dca348b',
                    name: 'Draft Job Description',
                    modifier: '',
                    icon: 'WRITE',
                    activityCategoryId: 'fabc9370-8ead-4c82-9a4d-a37f9a692d92'
                },
                {
                    activityTypeId: '9c0f98e9-dd1f-4aaa-8063-eb7b3fc5d48b',
                    name: 'Post Job Description',
                    modifier: '',
                    icon: 'RESUME',
                    activityCategoryId: 'fabc9370-8ead-4c82-9a4d-a37f9a692d92'
                },
                {
                    activityTypeId: 'f826863c-0ad3-4816-a6d8-3b6b89aa2a9f',
                    name: 'Review Resume',
                    modifier: '',
                    icon: 'RESUME',
                    activityCategoryId: 'fabc9370-8ead-4c82-9a4d-a37f9a692d92'
                },
                {
                    activityTypeId: '08889721-7efb-4967-b890-8ca4dd3f83d6',
                    name: 'Prepare for Candidate Interview',
                    modifier: '',
                    icon: 'READ',
                    activityCategoryId: 'fabc9370-8ead-4c82-9a4d-a37f9a692d92'
                },
                {
                    activityTypeId: '8dbc6482-db99-44f8-b4a9-d920099c0750',
                    name: 'Interview Candidate',
                    modifier: '',
                    icon: 'HANDSHAKE',
                    activityCategoryId: 'fabc9370-8ead-4c82-9a4d-a37f9a692d92'
                },
                {
                    activityTypeId: '4d87f554-efdb-4c2e-9878-73a71ba3a438',
                    name: 'Review Candidate',
                    modifier: '',
                    icon: 'READ',
                    activityCategoryId: 'fabc9370-8ead-4c82-9a4d-a37f9a692d92'
                },
                {
                    activityTypeId: 'bc7ef80b-0d12-4e64-b85e-705bd4d6079e',
                    name: 'Make Job Offer',
                    modifier: 'to',
                    icon: 'SALES',
                    activityCategoryId: 'fabc9370-8ead-4c82-9a4d-a37f9a692d92'
                },
                {
                    activityTypeId: '57ab5ac1-181e-483e-b078-55264da3f962',
                    name: 'Employee Time',
                    modifier: 'with',
                    icon: 'USER',
                    activityCategoryId: 'fabc9370-8ead-4c82-9a4d-a37f9a692d92'
                },
                {
                    activityTypeId: '47d58bbc-c7de-4e73-a707-a1900007b86f',
                    name: 'Review Analytics',
                    modifier: 'with',
                    icon: 'ANALYTICS',
                    activityCategoryId: 'fabc9370-8ead-4c82-9a4d-a37f9a692d92'
                },
                // Sales
                {
                    activityTypeId: '7a87b303-ce41-428e-98b2-bfdca74d2a83',
                    name: 'Call',
                    modifier: 'with',
                    icon: 'PHONE',
                    activityCategoryId: 'f8f862c4-fa5c-49a7-aea3-9664d085fbe7'
                },
                {
                    activityTypeId: '10a0596f-7f82-4615-a184-ba83b9f888a4',
                    name: 'Email',
                    modifier: 'to',
                    icon: 'EMAIL',
                    activityCategoryId: 'f8f862c4-fa5c-49a7-aea3-9664d085fbe7'
                },
                {
                    activityTypeId: 'e6fd32af-de93-481c-8f2e-d127ef161add',
                    name: 'Mail',
                    modifier: 'to/from',
                    icon: 'MAIL',
                    activityCategoryId: 'f8f862c4-fa5c-49a7-aea3-9664d085fbe7'
                },
                {
                    activityTypeId: '9d81a254-3b41-4ab4-a690-429ae6e79891',
                    name: 'Fax',
                    modifier: 'to/from',
                    icon: 'FAX',
                    activityCategoryId: 'f8f862c4-fa5c-49a7-aea3-9664d085fbe7'
                },
                {
                    activityTypeId: '52c4d090-6a75-45ad-b1d0-f5e3b8240166',
                    name: 'Meet',
                    modifier: 'with',
                    icon: 'HANDSHAKE',
                    activityCategoryId: 'f8f862c4-fa5c-49a7-aea3-9664d085fbe7'
                },
                {
                    activityTypeId: 'f2cbdd0e-f727-48ba-99e3-a771f0399bdf',
                    name: 'Smile and Dial',
                    modifier: 'with',
                    icon: 'SMILE_DIAL',
                    activityCategoryId: 'f8f862c4-fa5c-49a7-aea3-9664d085fbe7'
                },
                {
                    activityTypeId: '81a2ceb8-1d1f-4b49-92e2-bb759335c267',
                    name: 'Planning',
                    modifier: 'with',
                    icon: 'PLAN',
                    activityCategoryId: 'f8f862c4-fa5c-49a7-aea3-9664d085fbe7'
                },
                {
                    activityTypeId: '6f2832c0-c5c5-47d3-8ea3-ddc0957c4b40',
                    name: 'Review Analytics',
                    modifier: '',
                    icon: 'ANALYTICS',
                    activityCategoryId: 'f8f862c4-fa5c-49a7-aea3-9664d085fbe7'
                },
                {
                    activityTypeId: 'aea93c81-c896-4116-82f0-99864deef232',
                    name: 'Consultation',
                    modifier: 'with',
                    icon: 'HANDSHAKE',
                    activityCategoryId: 'f8f862c4-fa5c-49a7-aea3-9664d085fbe7'
                },
                {
                    activityTypeId: '6c996870-a020-47d0-a644-9068df7199cd',
                    name: 'Follow Up',
                    modifier: 'with',
                    icon: 'HANDSHAKE',
                    activityCategoryId: 'f8f862c4-fa5c-49a7-aea3-9664d085fbe7'
                },
                {
                    activityTypeId: '0c4e57ba-cb95-49c7-b3f8-6a5e385daf08',
                    name: 'Conflict Check',
                    modifier: 'with',
                    icon: 'CONFLICT',
                    activityCategoryId: 'f8f862c4-fa5c-49a7-aea3-9664d085fbe7'
                },
                // Marketing
                {
                    activityTypeId: '470031bd-f041-4543-8b01-6030ac9a7d76',
                    name: 'Call',
                    modifier: 'with',
                    icon: 'PHONE',
                    activityCategoryId: '038bd87f-2d35-431e-b5c8-7a31cf31a9f6'
                },
                {
                    activityTypeId: '71c9f406-1c88-4757-aca9-ac8c015abb98',
                    name: 'Email',
                    modifier: 'to',
                    icon: 'EMAIL',
                    activityCategoryId: '038bd87f-2d35-431e-b5c8-7a31cf31a9f6'
                },
                {
                    activityTypeId: '9cf0e68c-854d-4901-b73a-9172dbfab0be',
                    name: 'Mail',
                    modifier: 'to/from',
                    icon: 'MAIL',
                    activityCategoryId: '038bd87f-2d35-431e-b5c8-7a31cf31a9f6'
                },
                {
                    activityTypeId: 'b74728af-4411-4607-97e7-0eb024182286',
                    name: 'Fax',
                    modifier: 'to/from',
                    icon: 'FAX',
                    activityCategoryId: '038bd87f-2d35-431e-b5c8-7a31cf31a9f6'
                },
                {
                    activityTypeId: 'c8d9e780-8e2e-469e-a59c-2bd35408c4ff',
                    name: 'Design',
                    modifier: 'with',
                    icon: 'PENCIL',
                    activityCategoryId: '038bd87f-2d35-431e-b5c8-7a31cf31a9f6'
                },
                {
                    activityTypeId: 'f00cd735-095b-4314-b95f-a6712e9c586f',
                    name: 'Meet',
                    modifier: 'with',
                    icon: 'HANDSHAKE',
                    activityCategoryId: '038bd87f-2d35-431e-b5c8-7a31cf31a9f6'
                },
                {
                    activityTypeId: 'af48a3f7-f4a5-4294-b7d4-b00bfcd3a0fd',
                    name: 'Planning',
                    modifier: 'with',
                    icon: 'PLAN',
                    activityCategoryId: '038bd87f-2d35-431e-b5c8-7a31cf31a9f6'
                },
                {
                    activityTypeId: '726b0ac9-b0b7-4958-b08e-1f660e5adb2e',
                    name: 'Presentation',
                    modifier: 'with',
                    icon: 'PRESENT',
                    activityCategoryId: '038bd87f-2d35-431e-b5c8-7a31cf31a9f6'
                },
                {
                    activityTypeId: '8b691fe1-a5c1-48fb-ab1f-63f597984841',
                    name: 'Referral Development',
                    modifier: 'with',
                    icon: 'REFERRAL_OUT',
                    activityCategoryId: '038bd87f-2d35-431e-b5c8-7a31cf31a9f6'
                },
                {
                    activityTypeId: 'bf8b75dc-e36a-4561-aeb3-59504c5899ae',
                    name: 'Review Analytics',
                    modifier: '',
                    icon: 'ANALYTICS',
                    activityCategoryId: '038bd87f-2d35-431e-b5c8-7a31cf31a9f6'
                },
            ],
            maps: [{
                name: 'activityCategoryId',
                keysFn: t => t.activityCategoryId,
                presentFn: (t, k) => t.activityCategoryId === k
            }, ]
        });
    }

}
export default ActivityType;
